import { Link, Navigate, useParams } from "react-router-dom";
import { ArrowLeft, ArrowRight, Download, FileText, PackageCheck } from "lucide-react";
import Seo from "../components/Seo";
import { Reveal, Stagger, StaggerItem } from "../components/Reveal";
import { useProducts } from "../hooks/useProducts";
import { SITE } from "../data/site";

const ProductCategory = () => {
  const { slug } = useParams();
  const { data: products } = useProducts();
  const c = products.find((x) => x.slug === slug);
  if (!c) return <Navigate to="/products" replace />;

  const others = products.filter((x) => x.slug !== c.slug).slice(0, 3);

  return (
    <>
      <Seo
        title={`${c.name} | 3M Cable Accessories — Universal Engineers, Delhi`}
        description={`${c.name} from ${c.rangeLabel}: ${c.short} Authorised 3M channel partner supplying PAN India from Bhagirath Palace, Delhi — same-day NCR dispatch, 24–48h North India. Enquire for a quote by next working day.`}
        keywords={`${c.name} supplier Delhi, 3M ${c.name} distributor India, ${c.voltages.join(", ")} cable accessories Delhi NCR, cable jointing solution provider`}
        path={`/products/${c.slug}`}
        jsonld={{
          "@context": "https://schema.org",
          "@type": "Product",
          name: `${c.name} (3M Cable Accessories)`,
          category: c.name,
          description: c.blurb,
          brand: { "@type": "Brand", name: "3M" },
          image: `${SITE.domain}${c.image}`,
          url: `${SITE.domain}/products/${c.slug}`,
        }}
      />

      <section className="grain relative overflow-hidden bg-navy pb-16 pt-32 sm:pb-20 lg:pt-40" data-testid="category-hero">
        <div className="bp-grid absolute inset-0" />
        <div className="relative z-10 mx-auto max-w-7xl px-5 sm:px-8">
          <Reveal>
            <nav className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500" data-testid="category-breadcrumb">
              <Link to="/" className="hover:text-safety">Home</Link>
              <span>/</span>
              <Link to="/products" className="hover:text-safety">Products</Link>
              <span>/</span>
              <span className="text-slate-300">{c.name}</span>
            </nav>
          </Reveal>
          <Reveal delay={0.08}>
            <h1 className="mt-8 max-w-3xl font-display text-4xl font-black uppercase leading-[1.05] tracking-tight text-white sm:text-5xl lg:text-6xl">
              {c.name}
            </h1>
            <p className="mt-5 font-mono text-xs uppercase tracking-[0.25em] text-safety sm:text-sm" data-testid="category-voltage-range">
              {c.rangeLabel}
            </p>
          </Reveal>
        </div>
      </section>

      <section className="py-16 sm:py-20" data-testid="category-detail">
        <div className="mx-auto grid max-w-7xl gap-12 px-5 sm:px-8 lg:grid-cols-[0.95fr_1.05fr]">
          <Reveal className="lg:sticky lg:top-28 lg:self-start">
            <div className="relative">
              <div className="absolute -left-4 -top-4 h-full w-full border-2 border-safety" />
              <img src={c.image} alt={c.alt} className="clip-notch relative aspect-[4/3] w-full bg-navy object-contain" data-testid="category-image" />
              <div className="absolute -bottom-5 left-6 flex items-center gap-2 bg-navy px-4 py-2.5" data-testid="category-stock-badge">
                <PackageCheck size={16} className="text-safety" />
                <span className="font-mono text-[10px] font-semibold uppercase tracking-[0.18em] text-white">
                  Ready Stock · Dispatches in 24h
                </span>
              </div>
            </div>
          </Reveal>

          <div>
            <Reveal>
              <p className="leading-relaxed text-slate-600">{c.blurb}</p>
              <div className="mt-6 flex flex-wrap gap-2" data-testid="category-type-chips">
                {c.types.map((t) => (
                  <span key={t} className="border border-slate-300 px-3 py-1.5 font-mono text-[10px] uppercase tracking-[0.14em] text-slate-700">
                    {t}
                  </span>
                ))}
              </div>
            </Reveal>

            {!c.tapes && (
              <Reveal delay={0.08}>
                <h2 className="mt-10 font-mono text-[11px] uppercase tracking-[0.25em] text-safety">Technical Specifications</h2>
                <div className="mt-4 border border-slate-200" data-testid="category-specs-table">
                  {c.specs.map((s, i) => (
                    <div key={s.k} className={`grid grid-cols-1 gap-1 p-4 sm:grid-cols-[200px_1fr] sm:gap-4 sm:p-5 ${i % 2 ? "bg-slate-50" : "bg-white"}`}>
                      <p className="font-mono text-[10px] uppercase tracking-[0.18em] text-slate-500 sm:pt-0.5">{s.k}</p>
                      <p className="text-sm font-medium text-navy">{s.v}</p>
                    </div>
                  ))}
                </div>
              </Reveal>
            )}

            <Reveal delay={0.12}>
              <h2 className="mt-10 font-mono text-[11px] uppercase tracking-[0.25em] text-safety">Applications</h2>
              <ul className="mt-4 space-y-2.5">
                {c.applications.map((a) => (
                  <li key={a} className="flex items-start gap-3 text-sm leading-relaxed text-slate-700">
                    <span className="mt-1.5 inline-block h-2 w-2 shrink-0 bg-safety" />
                    {a}
                  </li>
                ))}
              </ul>
            </Reveal>

            <Reveal delay={0.16}>
              <div className="mt-10 flex flex-wrap items-center gap-4">
                <Link
                  to={`/enquiry?product=${c.slug}`}
                  data-testid={`product-enquire-button-${c.slug}`}
                  className="group flex items-center gap-2.5 bg-safety px-7 py-4 font-display text-sm font-bold uppercase tracking-[0.12em] text-white transition-colors hover:bg-navy"
                >
                  Enquire about this product
                  <ArrowRight size={16} className="transition-transform group-hover:translate-x-1" />
                </Link>
              </div>

              {c.datasheets?.length > 0 ? (
                <div className="mt-8" data-testid="datasheets-section">
                  <h2 className="font-mono text-[11px] uppercase tracking-[0.25em] text-safety">Technical datasheets</h2>
                  <div className="mt-4 grid gap-3 sm:grid-cols-2">
                    {c.datasheets.map((d) => (
                      <a
                        key={d.file}
                        href={d.file}
                        download
                        target="_blank"
                        rel="noopener noreferrer"
                        data-testid={`datasheet-link-${d.code}`}
                        className="group flex items-center justify-between gap-3 border border-slate-200 bg-white px-4 py-3.5 transition-all hover:border-safety hover:shadow-md"
                      >
                        <span className="flex items-center gap-3">
                          <FileText size={18} className="shrink-0 text-safety" />
                          <span>
                            <span className="block font-display text-sm font-bold text-navy">{d.label}</span>
                            <span className="block font-mono text-[9px] uppercase tracking-[0.14em] text-slate-500">{d.code} · PDF</span>
                          </span>
                        </span>
                        <Download size={16} className="shrink-0 text-navy transition-all group-hover:translate-y-0.5 group-hover:text-safety" />
                      </a>
                    ))}
                  </div>
                </div>
              ) : (
                <button
                  type="button"
                  disabled
                  data-testid={`product-datasheet-button-${c.slug}`}
                  title="Datasheet library coming soon"
                  className="flex cursor-not-allowed items-center gap-2 border border-slate-300 px-6 py-4 font-mono text-[11px] uppercase tracking-[0.16em] text-slate-400"
                >
                  <Download size={14} /> Datasheet — soon
                </button>
              )}
            </Reveal>
          </div>
        </div>
      </section>

      {c.tapes?.length > 0 && (
        <section className="border-y border-slate-200 bg-slate-50 py-16 sm:py-20" data-testid="category-range">
          <div className="mx-auto max-w-7xl px-5 sm:px-8">
            <Reveal>
              <p className="flex items-center gap-3 font-mono text-[10px] uppercase tracking-[0.3em] text-safety">
                <span className="inline-block h-2 w-2 bg-safety" /> The Range
              </p>
              <h2 className="mt-4 font-display text-3xl font-extrabold tracking-tight text-navy sm:text-4xl">
                Twelve 3M tapes. One shelf in Delhi.
              </h2>
            </Reveal>
            <Stagger className="mt-10 grid gap-5 sm:grid-cols-2 lg:grid-cols-3" delay={0.04}>
              {c.tapes.map((t, i) => (
                <StaggerItem key={t.name}>
                  <div className="group flex h-full flex-col border border-slate-200 bg-white transition-all hover:-translate-y-1 hover:border-safety hover:shadow-lg" data-testid="range-tape-card">
                    <div className="relative overflow-hidden bg-navy">
                      <img
                        src={t.img}
                        alt={t.name}
                        loading="lazy"
                        className="aspect-[4/3] w-full object-cover transition-transform duration-700 group-hover:scale-105"
                      />
                      <span className="absolute right-2.5 top-2.5 bg-navy/85 px-2 py-0.5 font-mono text-[9px] text-white">
                        {String(i + 1).padStart(2, "0")}
                      </span>
                    </div>
                    <div className="flex flex-1 flex-col p-5">
                      <h3 className="font-display text-base font-bold leading-snug text-navy">{t.name}</h3>
                      <p className="mt-1.5 font-mono text-[9px] uppercase tracking-[0.16em] text-safety">{t.tagline}</p>
                      <p className="mt-2.5 flex-1 text-sm leading-relaxed text-slate-600">{t.desc}</p>
                    </div>
                  </div>
                </StaggerItem>
              ))}
            </Stagger>
          </div>
        </section>
      )}

      {c.tapeSpecs?.length > 0 && (
        <section className="border-t border-slate-200 bg-white py-16 sm:py-20" data-testid="tapes-spec-table-section">
          <div className="mx-auto max-w-7xl px-5 sm:px-8">
            <Reveal>
              <p className="flex items-center gap-3 font-mono text-[10px] uppercase tracking-[0.3em] text-safety">
                <span className="inline-block h-2 w-2 bg-safety" /> Technical Specifications
              </p>
              <h2 className="mt-4 font-display text-3xl font-extrabold tracking-tight text-navy sm:text-4xl">
                All twelve tapes, compared.
              </h2>
            </Reveal>
            <Reveal delay={0.08}>
              <div className="mt-8 overflow-x-auto border border-slate-200" data-testid="tapes-spec-table">
                <table className="w-full min-w-[760px] border-collapse text-left">
                  <thead>
                    <tr className="bg-navy text-white">
                      {["Product", "Material & system", "Key performance", "Primary use"].map((h) => (
                        <th key={h} className="px-4 py-3.5 font-mono text-[10px] font-semibold uppercase tracking-[0.18em]">{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {c.tapeSpecs.map((t, i) => (
                      <tr key={t.product} className={`${i % 2 ? "bg-slate-50" : "bg-white"} transition-colors hover:bg-safety-tint`} data-testid={`tapes-spec-row-${i + 1}`}>
                        <td className="px-4 py-3.5 align-top font-display text-sm font-bold text-navy">{t.product}</td>
                        <td className="px-4 py-3.5 align-top text-sm text-slate-600">{t.material}</td>
                        <td className="px-4 py-3.5 align-top text-sm text-slate-600">{t.performance}</td>
                        <td className="px-4 py-3.5 align-top text-sm text-slate-600">{t.use}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <p className="mt-3 font-mono text-[9px] uppercase tracking-[0.18em] text-slate-400">
                Family-level guidance · full per-kit datasheets available on request
              </p>
            </Reveal>
          </div>
        </section>
      )}

      <section className="bg-slate-50 py-16 sm:py-20" data-testid="category-others">
        <div className="mx-auto max-w-7xl px-5 sm:px-8">
          <Reveal className="flex items-center justify-between gap-6">
            <h2 className="font-display text-2xl font-extrabold tracking-tight text-navy sm:text-3xl">Explore other lines</h2>
            <Link to="/products" data-testid="category-back-to-products" className="group flex items-center gap-2 font-mono text-xs uppercase tracking-[0.2em] text-navy hover:text-safety">
              <ArrowLeft size={15} className="transition-transform group-hover:-translate-x-1" /> All products
            </Link>
          </Reveal>
          <Stagger className="mt-8 grid gap-5 sm:grid-cols-3" delay={0.05}>
            {others.map((o) => (
              <StaggerItem key={o.slug}>
                <Link to={`/products/${o.slug}`} data-testid={`category-other-card-${o.slug}`} className="group flex h-full flex-col border border-slate-200 bg-white transition-all hover:-translate-y-1 hover:border-safety">
                  <div className="relative overflow-hidden bg-navy">
                    <img src={o.image} alt={o.alt} loading="lazy" className="aspect-[16/9] w-full object-cover opacity-90 transition-transform duration-700 group-hover:scale-105" />
                  </div>
                  <div className="flex flex-1 flex-col p-5">
                    <p className="font-mono text-[9px] uppercase tracking-[0.18em] text-slate-500">{o.rangeLabel}</p>
                    <h3 className="mt-1.5 flex-1 font-display text-base font-bold text-navy">{o.name}</h3>
                    <span className="mt-3 flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.2em] text-safety">
                      View <ArrowRight size={13} />
                    </span>
                  </div>
                </Link>
              </StaggerItem>
            ))}
          </Stagger>
        </div>
      </section>
    </>
  );
};

export default ProductCategory;
